import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, Package, Upload, X, ImageIcon, Archive, ArchiveRestore, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function ProductsPage() {
    const { t } = useLanguage();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [deleteErrorDialogOpen, setDeleteErrorDialogOpen] = useState(false);
    const [deleteErrorData, setDeleteErrorData] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [formData, setFormData] = useState({
        sku: '',
        name: '',
        description: '',
        categoryId: '',
        imageUrl: ''
    });
    const [newCategoryName, setNewCategoryName] = useState('');

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchData();
    }, [showArchived]);

    const fetchData = async () => {
        try {
            const [productsRes, categoriesRes] = await Promise.all([
                axios.get(`${API_URL}/products${showArchived ? '?includeArchived=true' : ''}`, { headers }),
                axios.get(`${API_URL}/categories`, { headers })
            ]);
            setProducts(productsRes.data.data.products);
            setCategories(categoriesRes.data.data.categories);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const uploadData = new FormData();
            uploadData.append('image', file);

            const res = await axios.post(`${API_URL}/products/upload-image`, uploadData, {
                headers: {
                    ...headers,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (res.data.success) {
                setFormData(prev => ({ ...prev, imageUrl: res.data.data.imageUrl }));
            }
        } catch (error) {
            console.error('Failed to upload image:', error);
            toast.error('Failed to upload image. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingProduct) {
                await axios.put(`${API_URL}/products/${editingProduct.id}`, formData, { headers });
            } else {
                await axios.post(`${API_URL}/products`, formData, { headers });
            }
            setDialogOpen(false);
            setEditingProduct(null);
            setFormData({ sku: '', name: '', description: '', categoryId: '', imageUrl: '' });
            fetchData();
        } catch (error) {
            console.error('Failed to save product:', error);
            const backendError = error.response?.data?.error || error.message;
            toast.error(`Error saving product: ${typeof backendError === 'string' ? backendError : JSON.stringify(backendError)}`);
        }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            sku: product.sku,
            name: product.name,
            description: product.description || '',
            categoryId: product.categoryId,
            imageUrl: product.imageUrl || ''
        });
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('deleteProductConfirm'))) return;
        try {
            await axios.delete(`${API_URL}/products/${id}`, { headers });
            fetchData();
            toast.success(t('successDeleted'));
        } catch (error) {
            console.error('Failed to delete product:', error);
            if (error.response?.status === 400) {
                const product = products.find(p => p.id === id);
                const details = error.response?.data?.details || {};
                if (product) {
                    setDeleteErrorData({
                        product,
                        inventoryUnits: details.inventoryUnits ?? product.stock?.total ?? 0,
                        transactions: details.transactions ?? 0
                    });
                    setDeleteErrorDialogOpen(true);
                } else {
                    toast.error(error.response?.data?.error || 'Cannot delete this product');
                }
            } else {
                toast.error(error.response?.data?.error || 'Failed to delete product');
            }
        }
    };

    const handleArchive = async (product) => {
        const isArchiving = product.isActive;
        const msg = isArchiving ? t('archiveProductConfirm') : t('restoreProductConfirm');
        if (!window.confirm(msg)) return;
        try {
            await axios.patch(`${API_URL}/products/${product.id}/archive`, { archive: isArchiving }, { headers });
            toast.success(isArchiving ? t('productArchived') : t('productRestored'));
            fetchData();
            setDeleteErrorDialogOpen(false);
        } catch (error) {
            console.error('Failed to archive product:', error);
            toast.error(error.response?.data?.error || 'Failed to archive product');
        }
    };

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/categories`, { name: newCategoryName }, { headers });
            setNewCategoryName('');
            setCategoryDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Failed to create category:', error);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            {/* Cannot-Delete Error Dialog */}
            {deleteErrorData && (
                <Dialog open={deleteErrorDialogOpen} onOpenChange={setDeleteErrorDialogOpen}>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-destructive">
                                <AlertTriangle className="h-5 w-5 shrink-0" />
                                {t('cannotDeleteProductTitle')}
                            </DialogTitle>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            {/* Product chip */}
                            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                                {deleteErrorData.product.imageUrl ? (
                                    <img src={deleteErrorData.product.imageUrl} alt="" className="h-10 w-10 rounded object-cover" />
                                ) : (
                                    <div className="h-10 w-10 rounded bg-background flex items-center justify-center">
                                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                )}
                                <div>
                                    <p className="font-semibold leading-tight">{deleteErrorData.product.name}</p>
                                    <p className="text-xs text-muted-foreground font-mono">{deleteErrorData.product.sku}</p>
                                </div>
                            </div>

                            {/* Why deletion is blocked */}
                            <div>
                                <p className="text-sm text-muted-foreground mb-2">{t('cannotDeleteProductMsg')}</p>
                                <p className="text-sm font-medium mb-1">{t('cannotDeleteProductReason')}</p>
                                <ul className="space-y-1 text-sm">
                                    {deleteErrorData.inventoryUnits > 0 && (
                                        <li className="flex items-center gap-2 text-muted-foreground">
                                            <span className="h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                                            {t('cannotDeleteReasonUnits').replace('{0}', deleteErrorData.inventoryUnits)}
                                        </li>
                                    )}
                                    {deleteErrorData.transactions > 0 && (
                                        <li className="flex items-center gap-2 text-muted-foreground">
                                            <span className="h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                                            {t('cannotDeleteReasonTransactions').replace('{0}', deleteErrorData.transactions)}
                                        </li>
                                    )}
                                    <li className="flex items-center gap-2 text-muted-foreground">
                                        <span className="h-1.5 w-1.5 rounded-full bg-destructive shrink-0" />
                                        {t('cannotDeleteReasonOrders')}
                                    </li>
                                </ul>
                            </div>

                            {/* Recommended action: Archive */}
                            <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
                                <p className="text-sm font-semibold flex items-center gap-2">
                                    <Archive className="h-4 w-4" />
                                    {t('archiveInstead')}
                                </p>
                                <p className="text-sm text-muted-foreground">{t('archiveInsteadDesc')}</p>
                                <Button
                                    size="sm"
                                    className="w-full mt-1"
                                    onClick={() => handleArchive(deleteErrorData.product)}
                                >
                                    <Archive className="h-4 w-4 mr-2" />
                                    {t('archiveProduct')}
                                </Button>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button variant="ghost" onClick={() => setDeleteErrorDialogOpen(false)}>
                                {t('cancel')}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">{t('products')}</h1>
                        <p className="text-muted-foreground">{t('productsSubtitle')}</p>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setShowArchived(prev => !prev)}
                        >
                            {showArchived ? (
                                <><ArchiveRestore className="h-4 w-4 mr-2" />{t('hideArchived')}</>
                            ) : (
                                <><Archive className="h-4 w-4 mr-2" />{t('showArchived')}</>
                            )}
                        </Button>
                        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline">
                                    <Plus className="h-4 w-4 mr-2" />
                                    {t('category')}
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <form onSubmit={handleCreateCategory}>
                                    <DialogHeader>
                                        <DialogTitle>{t('newCategory')}</DialogTitle>
                                        <DialogDescription>{t('addCategoryDesc')}</DialogDescription>
                                    </DialogHeader>
                                    <div className="py-4">
                                        <Label htmlFor="categoryName">{t('categoryName')}</Label>
                                        <Input
                                            id="categoryName"
                                            value={newCategoryName}
                                            onChange={(e) => setNewCategoryName(e.target.value)}
                                            placeholder={t('placeholderCategory')}
                                            required
                                        />
                                    </div>
                                    <DialogFooter>
                                        <Button type="submit">{t('createCategory')}</Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>

                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) {
                                setEditingProduct(null);
                                setFormData({ sku: '', name: '', description: '', categoryId: '', imageUrl: '' });
                            }
                        }}>
                            <DialogTrigger asChild>
                                <Button>
                                    <Plus className="h-4 w-4 mr-2" />
                                    {t('product')}
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <form onSubmit={handleSubmit}>
                                    <DialogHeader>
                                        <DialogTitle>{editingProduct ? t('editProduct') : t('newProduct')}</DialogTitle>
                                        <DialogDescription>
                                            {editingProduct ? t('updateProductDesc') : t('addProductDesc')}
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                        {/* Image Upload */}
                                        <div>
                                            <Label>{t('productImage') || 'Product Image'}</Label>
                                            <div className="mt-2">
                                                {formData.imageUrl ? (
                                                    <div className="relative inline-block">
                                                        <img
                                                            src={formData.imageUrl}
                                                            alt="Product"
                                                            className="h-32 w-32 object-cover rounded-lg border border-border"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                                                            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/80"
                                                        >
                                                            <X className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <label className="flex flex-col items-center justify-center h-32 w-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary transition-colors">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handleImageUpload}
                                                            className="hidden"
                                                            disabled={uploading}
                                                        />
                                                        {uploading ? (
                                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                                        ) : (
                                                            <>
                                                                <Upload className="h-6 w-6 text-muted-foreground mb-1" />
                                                                <span className="text-xs text-muted-foreground">
                                                                    {t('uploadImage') || 'Upload'}
                                                                </span>
                                                            </>
                                                        )}
                                                    </label>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <Label htmlFor="sku">{t('sku')}</Label>
                                            <Input
                                                id="sku"
                                                value={formData.sku}
                                                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                                                placeholder={t('placeholderSku')}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="name">{t('name')}</Label>
                                            <Input
                                                id="name"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                placeholder={t('placeholderProduct')}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="description">{t('description')}</Label>
                                            <Input
                                                id="description"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                placeholder={t('description')}
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="category">{t('category')}</Label>
                                            <Select
                                                value={formData.categoryId}
                                                onValueChange={(value) => setFormData({ ...formData, categoryId: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder={t('selectCategory')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {categories.map((cat) => (
                                                        <SelectItem key={cat.id} value={cat.id}>
                                                            {cat.name}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button type="submit">{editingProduct ? t('update') : t('create')}</Button>
                                    </DialogFooter>
                                </form>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* Products Table */}
                <div className="border rounded-lg">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[60px]"></TableHead>
                                <TableHead>{t('sku')}</TableHead>
                                <TableHead>{t('name')}</TableHead>
                                <TableHead>{t('category')}</TableHead>
                                <TableHead>{t('stock')}</TableHead>
                                <TableHead className="text-right">{t('actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {products.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                        <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        {t('noProducts')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                products.map((product) => (
                                    <TableRow key={product.id} className={!product.isActive ? 'opacity-50' : ''}>
                                        <TableCell>
                                            {product.imageUrl ? (
                                                <img
                                                    src={product.imageUrl}
                                                    alt={product.name}
                                                    className="h-10 w-10 object-cover rounded-md"
                                                />
                                            ) : (
                                                <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center">
                                                    <ImageIcon className="h-5 w-5 text-muted-foreground" />
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                                        <TableCell className="font-medium">
                                            <div className="flex items-center gap-2">
                                                {product.name}
                                                {!product.isActive && (
                                                    <Badge variant="outline" className="text-xs text-muted-foreground">
                                                        {t('archived')}
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary">{product.category?.name}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-1">
                                                <Badge variant="success">{product.stock?.available || 0} {t('available')}</Badge>
                                                {product.stock?.ordered > 0 && (
                                                    <Badge variant="warning">{product.stock.ordered} {t('ordered')}</Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                {product.isActive && (
                                                    <Button variant="ghost" size="icon" onClick={() => handleEdit(product)}>
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    title={product.isActive ? t('archiveProduct') : t('restoreProduct')}
                                                    onClick={() => handleArchive(product)}
                                                >
                                                    {product.isActive
                                                        ? <Archive className="h-4 w-4 text-muted-foreground" />
                                                        : <ArchiveRestore className="h-4 w-4 text-primary" />
                                                    }
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </Layout>
    );
}
